'use strict';

const libPictView = require('pict-view');

const html = String.raw;

// The orchestrator provider (entity catalog + schema/push seams + wizard lifecycle) and the mount the
// per-entity wizard renders into.
const PROVIDER_HASH = 'DataImportManager';

/**
 * Host page for the generic Meadow-entity importer.
 *
 * The screen is a thin shell: a target-entity picker + a mount div for the embedded
 * pict-section-dataimport wizard. Behaviour lives in the DataImportManager provider — picking an entity
 * (re)configures the wizard with a Headlight schema source + push target, and the wizard drives the
 * upload -> validate -> map -> generate -> push flow against the entity's live `/1.0/<Entity>/Schema`.
 * Like DataMapper / DataExplorer, the wizard is (re)mounted in onAfterRender because the page's main
 * render replaces the container each visit, so the mount div is only in the DOM after this paint.
 */
class DataImport extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get manager()
	{
		return this.pict.providers[PROVIDER_HASH];
	}

	onBeforeRender(pRenderable)
	{
		if (this.manager)
		{
			this.manager.prepare();
		}
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable)
	{
		this.pict.CSSMap.injectCSS();
		if (this.manager)
		{
			this.manager.mountWizard();
		}
		return super.onAfterRender(pRenderable);
	}
}

module.exports = DataImport;

module.exports.default_configuration =
{
	ViewIdentifier: 'DataImport',
	DefaultRenderable: 'DataImportWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
.dataimport-head { display: flex; align-items: center; gap: 0.9rem; max-width: 1040px; margin: 0 auto 1.1rem; }
.dataimport-ic { display: inline-flex; align-items: center; justify-content: center; width: 2.6rem; height: 2.6rem; border-radius: 12px;
	background: var(--theme-color-background-tertiary, #eef1f5); color: var(--theme-color-brand-primary, #156dd1); font-size: 1.2rem; flex: 0 0 auto; }
.dataimport-title { font-size: 1.5rem; font-weight: 700; line-height: 1.1; color: var(--theme-color-text-primary, #1f2733); margin: 0; }
.dataimport-sub { font-size: 0.92rem; color: var(--theme-color-text-muted, #6b7686); margin: 0.15rem 0 0; }
.dataimport-panel { max-width: 1040px; margin: 0 auto; background: var(--theme-color-background-primary, #fff);
	border: 1px solid var(--theme-color-border-light, #eef1f5); border-radius: 12px; padding: 1rem 1.1rem; min-height: 60vh; }
.dataimport-picker { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem; margin-bottom: 0.4rem; }
.dataimport-picker-label { font-size: 0.9rem; font-weight: 600; color: var(--theme-color-text-secondary, #45506180); }
.dataimport-select { font-size: 0.95rem; padding: 0.42rem 0.6rem; border-radius: 8px; min-width: 16rem;
	border: 1px solid var(--theme-color-border-medium, #d5dbe3); background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-primary, #1f2733); }
.dataimport-selected { font-size: 0.9rem; color: var(--theme-color-text-muted, #6b7686); margin: 0.2rem 0 0.6rem; }
.dataimport-selected i { color: var(--theme-color-brand-primary, #156dd1); margin-right: 0.3rem; }
.dataimport-intro { background: var(--theme-color-background-tertiary, #f5f7fa); border: 1px dashed var(--theme-color-border-medium, #d5dbe3);
	border-radius: 10px; padding: 0.9rem 1rem; color: var(--theme-color-text-secondary, #455061); font-size: 0.92rem; }
.dataimport-intro p { margin: 0 0 0.4rem; }
.dataimport-intro-note { font-size: 0.85rem; color: var(--theme-color-text-muted, #6b7686); margin: 0; }
.dataimport-result { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; margin: 0.4rem 0 0.7rem; padding: 0.6rem 0.8rem;
	border-radius: 8px; background: var(--theme-color-state-success-bg, #e7f6ec); color: var(--theme-color-state-success-text, #1d7a3f); font-size: 0.92rem; }
.dataimport-result i { font-size: 1rem; }
.dataimport-result-link { margin-left: 0.2rem; font-weight: 600; color: var(--theme-color-brand-primary, #156dd1); text-decoration: none; }
.dataimport-result-link:hover { text-decoration: underline; }
.dataimport-error { display: flex; align-items: center; gap: 0.5rem; margin: 0.4rem 0 0.7rem; padding: 0.6rem 0.8rem;
	border-radius: 8px; background: var(--theme-color-state-warning-bg, #fdecec); color: var(--theme-color-state-warning-text, #b3261e); font-size: 0.92rem; }
.dataimport-missing { margin: 0.4rem 0 0.7rem; padding: 0.6rem 0.8rem; border-radius: 8px;
	background: var(--theme-color-state-warning-bg, #fdecec); color: var(--theme-color-state-warning-text, #b3261e); font-size: 0.92rem; }
.dataimport-mount { margin-top: 0.6rem; }
`,

	Templates:
	[
		{
			Hash: 'DataImport-Option-Template',
			Template: html`<option value="{~D:Record.Entity~}"{~NE:Record.Selected^ selected~}>{~D:Record.Label~}</option>`,
		},
		{
			Hash: 'DataImport-SelectedHint-Template',
			Template: html`<div class="dataimport-selected"><i class="{~D:Record.Icon~}"></i><strong>{~D:Record.Label~}</strong> — {~D:Record.Description~}</div>`,
		},
		{
			Hash: 'DataImport-Intro-Template',
			Template: html`
				<div class="dataimport-intro">
					<p>Choose a target entity above to begin. You'll upload a file (CSV, TSV, or Excel), map its columns onto the entity's fields, preview the result, then push the records to the platform.</p>
					<p class="dataimport-intro-note">Re-importing the same file updates the matching records instead of creating duplicates — matched by the key column you map to the record's GUID.</p>
				</div>`,
		},
		{
			Hash: 'DataImport-Result-Template',
			Template: html`<div class="dataimport-result"><i class="fas fa-circle-check"></i><span>{~D:Record.Message~}</span><a class="dataimport-result-link" href="{~D:Record.ViewRoute~}">{~D:Record.ViewLabel~} →</a></div>`,
		},
		{
			Hash: 'DataImport-Error-Template',
			Template: html`<div class="dataimport-error"><i class="fas fa-triangle-exclamation"></i><span>{~D:Record.Message~}</span></div>`,
		},
		{
			Hash: 'DataImport-Missing-Template',
			Template: html`<div class="dataimport-missing"><i class="fas fa-triangle-exclamation"></i> The data-import control is unavailable. Please reload the page.</div>`,
		},
		{
			Hash: 'DataImport-Template',
			Template: html`
				<section class="section">
					<div class="container">
						<div class="dataimport-head">
							<span class="dataimport-ic"><i class="fas fa-file-import"></i></span>
							<div>
								<h1 class="dataimport-title">Import Data</h1>
								<p class="dataimport-sub">Bulk-import reference records (Materials, Pay Items, …) from a CSV, TSV, or Excel file. Columns map onto the entity's live platform schema.</p>
							</div>
						</div>
						<div class="dataimport-panel">
							{~TS:DataImport-Missing-Template:AppData.DataImportUI.MissingSlot~}
							<div class="dataimport-picker">
								<label class="dataimport-picker-label" for="DataImport-EntitySelect">Import into</label>
								<select id="DataImport-EntitySelect" class="dataimport-select" onchange="_Pict.providers.${PROVIDER_HASH}.selectEntity(this.value)">
									<option value="">— Choose an entity —</option>
									{~TS:DataImport-Option-Template:AppData.DataImportUI.Entities~}
								</select>
							</div>
							{~TS:DataImport-SelectedHint-Template:AppData.DataImportUI.SelectedSlot~}
							{~TS:DataImport-Result-Template:AppData.DataImportUI.ResultSlot~}
							{~TS:DataImport-Error-Template:AppData.DataImportUI.ErrorSlot~}
							{~TS:DataImport-Intro-Template:AppData.DataImportUI.IntroSlot~}
							<div id="DataImport-Mount" class="dataimport-mount"></div>
						</div>
					</div>
				</section>`,
		},
	],

	Renderables:
	[
		{
			RenderableHash: 'DataImportWrap',
			TemplateHash: 'DataImport-Template',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
		},
	],
};
